import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ShareButtons from '../components/ShareButtons';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import useOGMeta from '../hooks/useOGMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shopSlug = searchParams.get('shop') || '';
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [imageSrcs, setImageSrcs] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        // Init variant selections
        const defaults = {};
        (data.variants || []).forEach(v => {
          if (v.options?.length > 0) defaults[v.type] = v.options[0];
        });
        setSelectedVariants(defaults);
      })
      .catch(() => navigate(shopSlug ? `/negozio/${shopSlug}` : '/account'))
      .finally(() => setLoading(false));
  }, [id, navigate, shopSlug]);

  // When coming from a shop, fetch lightweight shop info for the branded header
  useEffect(() => {
    if (!shopSlug) return;
    axios.get(`${API}/negozio/${shopSlug}`).then(({ data }) => {
      setShop(data.shop || data);
    }).catch(() => {});
  }, [shopSlug]);

  useEffect(() => {
    if (!product?.images?.length) return;
    const loadImages = async () => {
      // For detail page: show original/clean first, card last
      const sortedImages = [...product.images].sort((a, b) => {
        const order = { original: 0, clean: 1, card: 2 };
        return (order[a?.type] ?? 1) - (order[b?.type] ?? 1);
      });

      const srcs = await Promise.all(
        sortedImages.map(async (img) => {
          const imgData = typeof img === 'string' ? { storage_path: img } : img;
          if (imgData.external_url) return imgData.external_url;
          const path = imgData.storage_path;
          if (!path) return null;
          try {
            const res = await axios.get(`${API}/files/${path}`, { responseType: 'blob' });
            return URL.createObjectURL(res.data);
          } catch { return null; }
        })
      );
      setImageSrcs(srcs.filter(Boolean));
    };
    loadImages();
    return () => imageSrcs.filter(s => s && s.startsWith('blob:')).forEach(s => URL.revokeObjectURL(s));
  }, [product]);

  const addToCart = async () => {
    if (!user) {
      toast.error('Accedi per aggiungere al carrello');
      // Preserve shop context after login
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/account?next=${next}`);
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/cart/add`, {
        product_id: product.id,
        quantity: 1,
        selected_variants: selectedVariants
      }, { withCredentials: true });
      toast.success('Aggiunto al carrello!');
    } catch (e) {
      toast.error('Errore nell\'aggiunta al carrello');
    } finally {
      setAdding(false);
    }
  };

  const productUrl = product ? `${window.location.origin}/prodotto/${product.id}` : '';
  const ogImage = product?.images?.[0]?.external_url || '';

  useOGMeta({
    title: product ? `${product.title} - ${product.price?.toFixed(2)}€` : 'PhotoVendo',
    description: product?.description || '',
    image: ogImage,
    url: productUrl
  });

  if (loading) {
    return (
      <div className="pb-safe">
        <div className="aspect-square skeleton-shimmer" />
        <div className="p-4 space-y-4">
          <div className="h-8 skeleton-shimmer rounded w-3/4" />
          <div className="h-6 skeleton-shimmer rounded w-1/3" />
          <div className="h-20 skeleton-shimmer rounded" />
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div data-testid="product-detail-page" className="pb-safe bg-[#FDFBF7] min-h-screen">
      {/* Shop-branded mini header when arrived from a shop */}
      {shopSlug && shop && (
        <div className="bg-white border-b border-[#E8E3D9] px-4 py-2 flex items-center gap-3 sticky top-0 z-30">
          <button
            data-testid="back-to-shop-btn"
            onClick={() => navigate(`/negozio/${shopSlug}`)}
            className="w-9 h-9 rounded-full bg-[#F0EBE1] hover:bg-[#E8E3D9] flex items-center justify-center text-[#2A2624]"
            aria-label="Torna al negozio"
          >
            <ArrowLeft size={16} />
          </button>
          <Link to={`/negozio/${shopSlug}`} data-testid="product-shop-brand" className="text-sm font-bold text-[#2A2624] truncate flex-1" style={{ fontFamily: 'Outfit' }}>
            {shop.name}
          </Link>
        </div>
      )}

      {/* Image Gallery */}
      <div className="relative">
        {!shopSlug && (
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="absolute top-4 left-4 z-10 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md"
          >
            <ArrowLeft size={20} className="text-[#2A2624]" />
          </button>
        )}

        <div className="relative aspect-square bg-[#F0EBE1] overflow-hidden max-h-[80vh]">
          {imageSrcs.length > 0 ? (
            <img src={imageSrcs[currentImageIdx]} alt={product.title} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#877E78]">
              <ShoppingCart size={48} />
            </div>
          )}

          {imageSrcs.length > 1 && (
            <>
              <button
                data-testid="prev-image"
                onClick={() => setCurrentImageIdx(i => (i - 1 + imageSrcs.length) % imageSrcs.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                data-testid="next-image"
                onClick={() => setCurrentImageIdx(i => (i + 1) % imageSrcs.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center"
              >
                <ChevronRight size={18} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {imageSrcs.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIdx(i)}
                    className={`w-2 h-2 rounded-full transition-all ${i === currentImageIdx ? 'bg-[#C25934] w-4' : 'bg-white/60'}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 space-y-4">
        <div>
          {product.category && product.category !== 'altro' && (
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#A64B2B]">
              {product.category}
            </span>
          )}
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2624] mt-1" style={{ fontFamily: 'Outfit' }}>
            {product.title}
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-3xl font-bold text-[#C25934]" style={{ fontFamily: 'Outfit' }}>
            {product.price?.toFixed(2)} &euro;
          </span>
          {product.original_price && product.original_price > product.price && (
            <span className="text-lg text-[#877E78] line-through">
              {product.original_price.toFixed(2)} &euro;
            </span>
          )}
        </div>

        {product.description && (
          <p className="text-[#5C554F] text-base leading-relaxed" style={{ fontFamily: 'Manrope' }}>
            {product.description}
          </p>
        )}

        {/* Variants */}
        {product.variants?.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">Opzioni</h3>
            {product.variants.map((variant, vi) => (
              <div key={`${variant.type}-${vi}`} className="space-y-1.5">
                <label className="text-sm font-medium text-[#5C554F] capitalize">{variant.type}</label>
                <Select
                  value={selectedVariants[variant.type] || ''}
                  onValueChange={val => setSelectedVariants(prev => ({ ...prev, [variant.type]: val }))}
                >
                  <SelectTrigger data-testid={`variant-${variant.type}`} className="h-12 rounded-xl border-[#E8E3D9] bg-white">
                    <SelectValue placeholder={`Scegli ${variant.type}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {variant.options?.map((opt, oi) => (
                      <SelectItem key={`${opt}-${oi}`} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        {/* Add to Cart */}
        <Button
          data-testid="add-to-cart-btn"
          onClick={addToCart}
          disabled={adding}
          className="w-full h-14 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-base font-semibold transition-all duration-300 active:scale-95"
          style={{ fontFamily: 'Outfit' }}
        >
          <ShoppingCart size={20} className="mr-2" />
          {adding ? 'Aggiunta...' : 'Aggiungi al Carrello'}
        </Button>

        {/* Share */}
        <div className="pt-2">
          <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider mb-3">Condividi</h3>
          <ShareButtons product={product} productUrl={productUrl} shop={shop} kind="product" resourceId={product.id} />
        </div>
      </div>
    </div>
  );
}
